import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, CalendarClock, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { blobUrl } from "@/lib/scanOptions";
import { fmtDT } from "@/lib/plannerOptions";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DailyDigest = ({ onDone }) => {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/reminders/daily").then((r) => (r.data.show ? setD(r.data) : onDone())).catch(onDone); }, []);
  const close = () => { api.post("/reminders/daily/seen").catch(() => {}); setD(null); onDone(); };
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="daily-digest-popup">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-emerald-600" />Günlük hatırlatma</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {d?.items.length ? d.items.map((e) => (
            <div key={e.id} className="rounded-lg border p-2.5 text-sm"><div className="font-medium">{e.title}</div><div className="text-xs text-slate-500">{fmtDT(e.start)}{e.location ? ` · ${e.location}` : ""}</div></div>
          )) : <div className="text-sm text-slate-500">Önümüzdeki 3 günde randevu yok.</div>}
          {d?.open_todos > 0 && <div className="text-sm rounded-lg bg-amber-50 border border-amber-200 p-2.5">{d.open_todos} açık göreviniz var.</div>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" data-testid="daily-digest-todos" onClick={() => { close(); navigate("/todos"); }}>Görevler</Button>
          <Button data-testid="daily-digest-calendar" className="bg-slate-900" onClick={() => { close(); navigate("/calendar"); }}>Takvime git</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ReminderPopup = () => {
  const [due, setDue] = useState([]);
  useEffect(() => {
    const poll = () => api.get("/reminders/due").then((r) => r.data.length && setDue((x) => [...x, ...r.data])).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <Dialog open={due.length > 0} onOpenChange={(o) => !o && setDue([])}>
      <DialogContent data-testid="reminder-popup">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-amber-500" />Hatırlatma</DialogTitle></DialogHeader>
        {due.map((e) => <div key={e.id} className="rounded-lg border p-3 text-sm"><div className="font-semibold">{e.title}</div><div className="text-xs text-slate-500">{fmtDT(e.start)}{e.location ? ` · ${e.location}` : ""}</div></div>)}
        <div className="flex justify-end"><Button data-testid="reminder-ok" onClick={() => setDue([])}>Tamam</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const AdPopup = () => {
  const [ad, setAd] = useState(null);
  const location = useLocation();
  const busy = useRef(false);
  const show = async (trigger) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/ads/next", { params: { trigger } });
      if (!data) { busy.current = false; return; }
      const src = await blobUrl(`/ads/${data.id}/image`);
      setAd({ ...data, src });
      api.post(`/ads/${data.id}/view`).catch(() => {});
    } catch { busy.current = false; }
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if (!sessionStorage.getItem("ad_login_shown")) { sessionStorage.setItem("ad_login_shown", "1"); show("LOGIN"); return; }
      show(location.pathname === "/panel" ? "DASHBOARD" : "NAVIGATION");
    }, 300);
    return () => clearTimeout(t);
  }, [location.pathname]);
  const close = () => { setAd(null); busy.current = false; };
  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="ad-popup">
        {ad && <>
          <img src={ad.src} alt={ad.title} className="w-full max-h-[70vh] object-contain bg-slate-900" data-testid="ad-popup-image" />
          <div className="p-4 flex items-center justify-between gap-3">
            <div className="font-semibold">{ad.title}</div>
            {ad.link_url && <a href={ad.link_url} target="_blank" rel="noreferrer" data-testid="ad-popup-link"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"><ExternalLink className="w-4 h-4 mr-1" />İncele</Button></a>}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};

export const GlobalPopups = () => {
  const { user, subscription } = useAuth();
  const [digestDone, setDigestDone] = useState(false);
  if (!user?.office_id || subscription?.status === "FROZEN") return null;
  return <><DailyDigest onDone={() => setDigestDone(true)} />{digestDone && <><ReminderPopup /><AdPopup /></>}</>;
};
